import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';

// Types for the request and response
export interface SpotlightsRequest {
  intention: string;
  since?: string; // ISO date string
}

export interface SpotlightResource {
  assetId: string;
  id: string;
}

export type SpotlightSummaryStatus = 'dismissed' | null;
export interface StatusUpdatedBy {
  email: string;
  userId: string;
}

export interface Spotlight {
  id: string;
  batchId?: string;
  spotlightTypeId?: string;
  message: string;
  prompt: string;
  spotlightType: string;
  createdAt: string;
  status?: SpotlightSummaryStatus;
  statusUpdatedBy?: StatusUpdatedBy | null;
  spotlightResources?: SpotlightResource[];
  showOnPages: SpotlightPage[];
}
export enum SpotlightPage {
  RISKS = 'risks',
  ASSETS = 'assets',
  FINDINGS = 'findings',
  VULNERABILITIES = 'vulnerabilities',
  COMPLIANCE = 'compliance',
  HOME = 'home',
}

export enum SpotlightType {
  ASSETS_TRENDS = 'assets_trends',
  FINDINGS = 'findings',
  FINDINGS_TRENDS = 'findings_trends',
  VULNERABILITIES = 'vulnerabilities',
  VULNERABILITIES_TRENDS = 'vulnerabilities_trends',
  COMPLIANCE_TRENDS = 'compliance_trends',
  WELL_ARCHITECTED_TRENDS = 'well-architected_trends',
  RISKS = 'risks',
  RISKS_TRENDS = 'risks_trends',
  FEATURE = 'feature',
  HOME = 'home',
  SBOM_PACKAGES = 'sbom_packages',
}

export interface SpotlightsResponse {
  data: {
    spotlights: Spotlight[];
  };
  meta: {
    total: number;
  };
}

export interface SpotlightsError {
  message: string;
  error?: string;
}

// API function to fetch spotlights
export const fetchSpotlights = async (
  spotlightId?: string,
): Promise<SpotlightsResponse> => {
  const body = spotlightId ? { spotlightId } : {};
  const response = await api.post('/api/core/pleri/spotlights', body);
  return response.data;
};

// API function to fetch a single spotlight by id
export const fetchSpotlightById = async (
  id: string,
): Promise<Spotlight | undefined> => {
  const response = await api.post('/api/core/pleri/spotlights', {
    spotlightId: id,
  });
  const spotlights = response.data?.data?.spotlights || [];
  return spotlights.find((s: Spotlight) => s.id === id);
};

// React Query hook for spotlights (mutation)
export const useSpotlights = () => {
  return useMutation<SpotlightsResponse, SpotlightsError, void>({
    mutationFn: () => fetchSpotlights(),
  });
};

// React Query hook for spotlights (query)
export const useSpotlightsQuery = () => {
  return useQuery<SpotlightsResponse, SpotlightsError>({
    queryKey: ['spotlights'],
    queryFn: () => fetchSpotlights(),
    refetchOnWindowFocus: false,
    retry: false,
  });
};

// API function to dismiss a spotlight
export const dismissSpotlight = async (summaryId: string): Promise<void> => {
  await api.patch(`/api/core/pleri/spotlights/${summaryId}`, {
    status: 'dismissed',
  });
};

// React Query hook for dismissing a spotlight
export const useDismissSpotlight = () => {
  const queryClient = useQueryClient();
  return useMutation<void, SpotlightsError, string>({
    mutationFn: (summaryId: string) => dismissSpotlight(summaryId),
    onSuccess: (_, summaryId) => {
      // Update the cache instead of refetching
      const oldData = queryClient.getQueryData<SpotlightsResponse>([
        'spotlights',
      ]);
      if (!oldData) {
        return;
      }

      // Update the spotlight status in the cache
      const updatedSpotlights = oldData.data.spotlights.map((spotlight) => {
        if (spotlight.id === summaryId) {
          return {
            ...spotlight,
            status: 'dismissed' as SpotlightSummaryStatus,
            // Note: statusUpdatedBy will be set by the server on next fetch
            // For now, we just update the status optimistically
          };
        }
        return spotlight;
      });

      queryClient.setQueryData<SpotlightsResponse>(['spotlights'], {
        ...oldData,
        data: {
          ...oldData.data,
          spotlights: updatedSpotlights,
        },
      });
    },
  });
};
